/**
 * Prosumer Calculator - Imported Data Hook
 *
 * Bridges the existing "Import danych" module (useGetImportSummary,
 * useGetHourlyData) into the prosumer calculator's CSVImportData format.
 *
 * The import module stores hourly consumption data in DynamoDB.
 * This hook fetches it and transforms it into the same format used by CSV import.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useGetImportSummary } from '../../../hooks/useGetImportSummary';
import { useGetHourlyData } from '../../../hooks/useGetHourlyData';
import type { HourlyDataPoint } from '../../../hooks/useGetHourlyData';
import type { CSVImportData } from '../types';

// =============================================================================
// RETURN TYPE
// =============================================================================

export interface UseImportedDataReturn {
  /** Whether user has any imported data for the given year */
  hasImportedData: boolean;
  /** Number of months with available data (0-12) */
  availableMonths: number;
  /** Whether all 12 months are available */
  isFullYear: boolean;
  /** Sum of all monthly consumption from summary */
  totalConsumption: number;
  /** Transformed data in CSVImportData format (after loadData) */
  data: CSVImportData | null;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** Fetch all hourly data and transform to CSVImportData */
  loadData: () => Promise<CSVImportData | null>;
  /** Refetch the import summary (clears cached data, triggers fresh fetch) */
  refetch: () => Promise<void>;
  /** Clear loaded data and errors */
  reset: () => void;
}

// =============================================================================
// TRANSFORM HELPERS
// =============================================================================

function extractDayOfMonth(timestamp: string): number {
  // Timestamp format: "2025-06-28T23:00:00.000Z"
  const match = timestamp.match(/-(\d{2})T/);
  return match ? parseInt(match[1], 10) : 1;
}

function transformHourlyPoints(
  points: HourlyDataPoint[],
  monthIndex: number
): CSVImportData['hourlyConsumptionData'] {
  return points.map((p) => ({
    hour: p.hour,
    dayOfMonth: extractDayOfMonth(p.timestamp),
    month: monthIndex, // 0-based
    consumptionKwh: p.consumption,
  }));
}

// =============================================================================
// HOOK
// =============================================================================

export function useImportedData(year: number): UseImportedDataReturn {
  const summary = useGetImportSummary(year);
  const { getHourlyData } = useGetHourlyData();

  const [data, setData] = useState<CSVImportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear stale loaded data when the user selects a different year. The summary
  // hook (useGetImportSummary) re-fetches automatically on year change, but the
  // locally-loaded hourly CSV data stays stale without this effect.
  useEffect(() => {
    setData(null);
    setError(null);
  }, [year]);

  // Derived state from summary
  const hasImportedData = useMemo(() => {
    return !!(summary.data && summary.data.totalMonths > 0);
  }, [summary.data]);

  const availableMonths = useMemo(() => {
    return summary.data?.totalMonths ?? 0;
  }, [summary.data]);

  const isFullYear = availableMonths >= 12;

  const totalConsumption = useMemo(() => {
    if (!summary.data?.imports) return 0;
    return summary.data.imports.reduce((sum, m) => sum + m.totalConsumption, 0);
  }, [summary.data]);

  // Fetch all months' hourly data and transform
  const loadData = useCallback(async (): Promise<CSVImportData | null> => {
    if (!summary.data || summary.data.totalMonths === 0) {
      setError('Brak zaimportowanych danych dla wybranego roku');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const monthlyConsumptionKwh = new Array(12).fill(0);
      const monthlyExportKwh = new Array(12).fill(0); // always 0 — import module has consumption only
      const allHourlyData: CSVImportData['hourlyConsumptionData'] = [];

      // Fetch all available months in parallel
      const monthResults = await Promise.all(
        summary.data.imports.map((imp) => getHourlyData(imp.yearMonth))
      );

      for (let i = 0; i < monthResults.length; i++) {
        const result = monthResults[i];
        const yearMonth: string = summary.data.imports[i].yearMonth;

        // Extract 0-based month index from yearMonth "2025-03" → 2
        const monthIndex = parseInt(yearMonth.split('-')[1], 10) - 1;

        if (result && result.hourlyData) {
          // Aggregate monthly total
          monthlyConsumptionKwh[monthIndex] = result.hourlyData.reduce(
            (sum: number, p: HourlyDataPoint) => sum + p.consumption,
            0
          );

          // Transform hourly points
          const transformed = transformHourlyPoints(result.hourlyData, monthIndex);
          allHourlyData.push(...transformed);
        }
      }

      const csvData: CSVImportData = {
        monthlyConsumptionKwh,
        monthlyExportKwh,
        hasProsumerData: false, // import module has consumption only
        hourlyConsumptionData: allHourlyData,
        hourlyExportData: [],
      };

      setData(csvData);
      return csvData;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd podczas pobierania danych';
      setError(msg);
      setData(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [summary.data, getHourlyData]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  // Clears local state and forces a fresh summary fetch (e.g. after user imports data
  // in a different tab/session and returns to the IMPORTED tab).
  const refetch = useCallback(async () => {
    setData(null);
    setError(null);
    await summary.refetch(year);
  }, [summary.refetch, year]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    hasImportedData,
    availableMonths,
    isFullYear,
    totalConsumption,
    data,
    loading: loading || summary.loading,
    error: error || summary.error,
    loadData,
    refetch,
    reset,
  };
}
